import {ColumnRenderer} from '@xh/hoist/cmp/grid';
import {
    fmtBillions,
    fmtDate,
    fmtDateTime,
    fmtDateTimeSec,
    fmtMillions,
    fmtNumber,
    fmtPercent,
    fmtPrice,
    fmtQuantity,
    fmtThousands,
    fmtTime,
    NumberFormatOptions,
    Precision
} from '@xh/hoist/format';
import {isNil} from 'lodash';
import {ReactNode} from 'react';
import {FormatSpec} from './SpecTypes';

/**
 * Format registry - resolves a serializable FormatSpec to Hoist formatting. This is the only place
 * where a format descriptor becomes code, so grid cells and chart axes/tooltips configured by the
 * configurator or the AI share exactly the same vocabulary.
 */
export function formatValue(v: any, f: FormatSpec): ReactNode {
    return format(v, f, false);
}

/** Format as an HTML string - for Highcharts labels and tooltips. */
export function formatHtml(v: any, f: FormatSpec): string {
    const ret = format(v, f, true);
    return isNil(ret) ? '' : String(ret);
}

function format(v: any, f: FormatSpec, asHtml: boolean): ReactNode {
    if (!f || isNil(v)) return v;

    switch (f.type) {
        case 'number':
            return formatNumber(v, f, asHtml);
        case 'date':
            return formatDate(v, f, asHtml);
        case 'bool':
            return v ? 'Yes' : 'No';
        default:
            return v;
    }
}

/** Renderer for a column, or null to keep the host's default rendering. */
export function formatRenderer(f: FormatSpec): ColumnRenderer {
    if (!f || f.type === 'text') return null;
    return v => formatValue(v, f);
}

/** True if the rendered output is an element (e.g. colored numbers) rather than a plain string. */
export function formatIsComplex(f: FormatSpec): boolean {
    return f?.type === 'number' && !!f.colorSpec;
}

function formatNumber(v: any, f: FormatSpec, asHtml: boolean): ReactNode {
    if (typeof v !== 'number') return v;

    const {numberStyle = 'plain', precision, prefix, suffix, currency, colorSpec, ledger, zeroPad, withPlusSign} = f,
        opts: NumberFormatOptions = {};

    if (asHtml) opts.asHtml = true;
    // Only pass options that are set, so each style keeps its own Hoist defaults
    if (!isNil(precision)) opts.precision = precision as Precision;
    if (colorSpec) opts.colorSpec = true;
    if (ledger) opts.ledger = true;
    if (zeroPad) opts.zeroPad = true;
    if (withPlusSign) opts.withPlusSign = true;
    if (prefix) opts.prefix = prefix;
    else if (currency && numberStyle !== 'percent') opts.prefix = '$';
    if (suffix) opts.label = suffix;

    switch (numberStyle) {
        case 'thousands':
            return fmtThousands(v, opts);
        case 'millions':
            return fmtMillions(v, opts);
        case 'billions':
            return fmtBillions(v, opts);
        case 'percent':
            return fmtPercent(v, opts);
        case 'price':
            return fmtPrice(v, opts);
        case 'quantity':
            return fmtQuantity(v, opts);
        default:
            return fmtNumber(v, opts);
    }
}

function formatDate(v: any, f: FormatSpec, asHtml: boolean): ReactNode {
    const opts = asHtml ? {asHtml} : {};
    if (f.dateFmt) return fmtDate(v, {...opts, fmt: f.dateFmt});

    switch (f.dateStyle) {
        case 'dateTime':
            return fmtDateTime(v, opts);
        case 'dateTimeSec':
            return fmtDateTimeSec(v, opts);
        case 'time':
            return fmtTime(v, opts);
        default:
            return fmtDate(v, opts);
    }
}
